import { useCallback, useEffect, useState } from 'react';

import { stageRefusal, type LeadPatch } from '../lib/crm/leadSheet';
import { messageOf } from '../store/crm/writeResult';
import { supabase } from '../lib/supabase';
import { useCRMStore } from '../store/crmStore';
import type { OnboardingLead } from '../types/crm';

/**
 * The gestures a lead's sheet makes (PLAN-ENCRE-OUTILS O3 bis). Its stage moves only through the
 * back's gated writers — `advance_onboarding_stage` checks the stage's exit criteria and says the
 * missing ones, `reopen_onboarding_stage` asks a reason —, never by writing the column (the old
 * panel's slider skipped the criteria and the stage history, and told the client « étape atteinte »).
 * Its fields through the store (`updateOnboardingLead`, its own failure returned). A refusal is
 * said, never taken for a success; after each gesture the lead is read again.
 */
export function useLeadGestures(leadId: string | null, reload: () => Promise<void>) {
  const updateOnboardingLead = useCRMStore((s) => s.updateOnboardingLead);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A refusal belongs to the lead it was about.
  useEffect(() => setError(null), [leadId]);

  const run = useCallback(
    async (gesture: () => Promise<string | null>): Promise<boolean> => {
      setBusy(true);
      try {
        const failed = await gesture();
        setError(failed);
        // Busy until the lead is read again: a second click on the stage it just left would be
        // refused as « moved meanwhile » (relecture du 14/10/2026).
        await reload();
        return !failed;
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const save = useCallback(
    (patch: LeadPatch) =>
      run(async () => {
        if (!leadId || Object.keys(patch).length === 0) return null;
        const refused = await updateOnboardingLead(leadId, patch as Partial<OnboardingLead>);
        return refused ? `Pas enregistré : ${stageRefusal(refused)}` : null;
      }),
    [leadId, run, updateOnboardingLead],
  );

  const advance = useCallback(
    (expectedStage: number) =>
      run(async () => {
        if (!leadId) return null;
        const { error: rpcError } = await supabase.rpc('advance_onboarding_stage', { p_lead_id: leadId, p_expected_stage: expectedStage });
        return rpcError ? `Étape non avancée. ${stageRefusal(messageOf(rpcError, 'Refus sans message.'))}` : null;
      }),
    [leadId, run],
  );

  const reopen = useCallback(
    (toStage: number, reason: string) =>
      run(async () => {
        if (!leadId) return null;
        const { error: rpcError } = await supabase.rpc('reopen_onboarding_stage', { p_lead_id: leadId, p_to_stage: toStage, p_reason: reason.trim() });
        return rpcError ? `Étape non rouverte. ${stageRefusal(messageOf(rpcError, 'Refus sans message.'))}` : null;
      }),
    [leadId, run],
  );

  return { busy, error, save, advance, reopen };
}
